import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchAssetTrendDataInitialState } from './../../services/dashboard/AssetTrendDataService';

export interface ChartViewOptions {
  chartType: string;
  showTooltip: boolean;
  showSD: boolean;
  showEvents: boolean;
  isLogarithmic?: boolean;
}
const initialState: {
  data: any[] | undefined;
  loading: boolean;
  error: any | undefined;
  rawData: any[] | undefined;
  startDate: Date | null;
  endDate: Date | null;
  aggregate: string | null;
  chartViewOptions: ChartViewOptions;
  retrieveMinMax: boolean;
  fetchCov: boolean;
  isCustomRange?: boolean;
  lastFetchParams?: {
    assetId: string;
    startDate: Date;
    endDate: Date;
    aggregate: string;
    aggregateMethod: string;
    retrieveMinMax: boolean;
    fetchCov: boolean;
    Addresses?: string;
    isZoomedIn?: boolean;
  } | null;
  isLiveDataEnabled: boolean;
  enableLiveToggle: boolean;
  liveDataTimeRange: '24H' | '';
  isAnyChartZoomed: boolean;
} = {
  data: undefined,
  loading: false,
  error: undefined,
  rawData: undefined, // Add rawData to the state
  startDate: null, // Add startDate to the state
  endDate: null, // Add endDate to the state
  aggregate: null, // Add aggregate to the state
  chartViewOptions: {
    chartType: 'line',
    showTooltip: true,
    showSD: true,
    showEvents: true,
    isLogarithmic: false,
  } as ChartViewOptions, // Add chartViewOptions to the state
  retrieveMinMax: true, // Add retrieveMinMax to the state
  fetchCov: false,
  isCustomRange: false,
  lastFetchParams: null,
  isLiveDataEnabled: false,
  enableLiveToggle: false,
  liveDataTimeRange: '',
  isAnyChartZoomed: false,
};
export interface ChartViewOptions {
  chartType: string;
  showTooltip: boolean;
  showSD: boolean;
  showEvents: boolean;
  isLogarithmic?: boolean;
}

const AssetTrendDataSlice = createSlice({
  name: 'assetTrendDataSlice',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.data = undefined;
      state.rawData = undefined;
      state.error = undefined;
      state.loading = false;
      state.startDate = null;
      state.endDate = null;
      state.aggregate = null;
      state.chartViewOptions = {
        chartType: 'line',
        showTooltip: true,
        showSD: true,
        showEvents: true,
        isLogarithmic: false,
      } as ChartViewOptions;
      state.retrieveMinMax = true;
      state.fetchCov = false;
      state.lastFetchParams = null;
      state.isLiveDataEnabled = false;
      state.enableLiveToggle = false;
      state.liveDataTimeRange = '';
      state.isAnyChartZoomed = false;
    },
    resetAssetTrendDataState: (state) => {
      state.data = undefined;
      state.rawData = undefined;
      state.error = undefined;
      state.loading = false;
      state.startDate = null;
      state.endDate = null;
      state.aggregate = null;
      state.chartViewOptions = {
        chartType: 'line',
        showTooltip: true,
        showSD: true,
        showEvents: true,
        isLogarithmic: false,
      } as ChartViewOptions;
      state.retrieveMinMax = true;
      state.fetchCov = false;
      state.lastFetchParams = null;
      state.liveDataTimeRange = '';
      state.isAnyChartZoomed = false;
    },
    setDateRangeAndAggregate: (state, action) => {
      const { startDate, endDate, aggregate } = action.payload;
      state.startDate = startDate;
      state.endDate = endDate;
      state.aggregate = aggregate;
    },
    setChartViewOptions: (state, action) => {
      const { chartType, showTooltip, showSD, showEvents, isLogarithmic } = action.payload.chartViewOptions;
      state.chartViewOptions.chartType = chartType;
      state.chartViewOptions.showTooltip = showTooltip; // Update showTooltip
      state.chartViewOptions.showSD = showSD; // Update showSD
      state.chartViewOptions.showEvents = showEvents; // Update showEvents
      state.chartViewOptions.isLogarithmic = isLogarithmic;
    },
    setIsCustomRange: (state, action) => {
      state.isCustomRange = action.payload;
    },
    setLiveDataEnabled: (state, action) => {
      state.isLiveDataEnabled = action.payload;
    },
    setEnableLiveToggle: (state, action) => {
      state.enableLiveToggle = action.payload;
    },
    setLiveDataTimeRange: (state, action) => {
      state.liveDataTimeRange = action.payload;
    },
    setChartsZoomState: (state, action) => {
      state.isAnyChartZoomed = Boolean(action.payload);
    },
    addLiveDataPoints: (state, action) => {
      const { addressLiveDataMap } = action.payload;

      if (!state.data || !Array.isArray(state.data)) {
        return;
      }

      // let minNewDate: Date | null = null;
      let maxNewDate: Date | null = null;

      // Update each chart series that has matching addresses
      state.data = state.data.map((chartSeries: any) => {
        if (chartSeries.address && addressLiveDataMap[chartSeries.address]) {
          const liveDataPoint = addressLiveDataMap[chartSeries.address];
          console.log('Chart series address:', chartSeries.address);

          const updatedDataPoints = [...(chartSeries.dataPoints || [])];

          // Only copy over these properties if the existing data points have them
          if (updatedDataPoints.length > 0) {
            liveDataPoint.cov = updatedDataPoints[0]?.cov || 0;
            liveDataPoint.cumulativeCOV = updatedDataPoints[0]?.cumulativeCOV || 0;
            liveDataPoint.stdDevValue = updatedDataPoints[0]?.stdDevValue || 0;
            liveDataPoint.stdDevNegValue = updatedDataPoints[0]?.stdDevNegValue || 0;
            liveDataPoint.stdDevPosValue = updatedDataPoints[0]?.stdDevPosValue || 0;
          }

          // Track the date of the new live data point
          if (liveDataPoint && liveDataPoint.date) {
            const pointDate = new Date(liveDataPoint.date);
            // if (!minNewDate || pointDate < minNewDate) {
            //   minNewDate = pointDate;
            // }
            if (!maxNewDate || pointDate > maxNewDate) {
              maxNewDate = pointDate;
            }
          }

          // Add the new live data point
          updatedDataPoints.push({
            ...(typeof liveDataPoint === 'object' && liveDataPoint !== null ? liveDataPoint : {}),
            index: updatedDataPoints.length, // Set index to the new position
          });

          // Sort by date to maintain chronological order
          updatedDataPoints.sort((a: any, b: any) => a.date - b.date);

          // Update indices after sorting
          updatedDataPoints.forEach((point: any, idx: any) => {
            point.index = idx;
          });

          return {
            ...chartSeries,
            dataPoints: updatedDataPoints,
          };
        }

        return chartSeries;
      });

      // Update the date range to accommodate new live data points
      if (maxNewDate !== null) {
        state.endDate = maxNewDate;
        // if (state.liveDataTimeRange != '') {
        //   const timeRangeMs = state.liveDataTimeRange === '7D'
        //     ? 7 * 24 * 60 * 60 * 1000  // 7 days in milliseconds
        //     : 24 * 60 * 60 * 1000;     // 24 hours in milliseconds
        //   state.startDate = new Date((maxNewDate as Date).getTime() - timeRangeMs);
        // }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssetTrendInitialStateAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAssetTrendInitialStateAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.rawData = action.payload.rawData;
        state.lastFetchParams = action.meta.arg;
      })
      .addCase(fetchAssetTrendInitialStateAsync.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Async thunk to fetch the initial state data
export const fetchAssetTrendInitialStateAsync = createAsyncThunk(
  'assetTrendDataSlice/fetchInitialState',
  async (
    {
      assetId,
      startDate,
      endDate,
      aggregate,
      aggregateMethod,
      retrieveMinMax,
      fetchCov,
      chartType,
      //timeseriesChartData,
      Addresses,
    }: {
      assetId: string;
      startDate: Date;
      endDate: Date;
      aggregate: string;
      aggregateMethod: string;
      retrieveMinMax: boolean;
      timeseriesChartData: any;
      fetchCov: boolean;
      chartType?: string;
      Addresses?: string;
      isZoomedIn?: boolean;
    },
    thunkAPI,
  ) => {
    const state: any = thunkAPI.getState();
    const fetchStdDeviation = Boolean(state?.AssetTrendDataSlice?.chartViewOptions?.showSD);
    const isRodLift = chartType === 'rl-well-charts' || chartType === 'facility-well-charts';
    const response = await fetchAssetTrendDataInitialState(
      assetId,
      startDate,
      endDate,
      aggregate,
      aggregateMethod,
      retrieveMinMax,
      fetchStdDeviation,
      fetchCov,
      isRodLift,
      //timeseriesChartData,
      Addresses,
    );
    return response;
  },
  {
    condition: (params, { getState }) => {
      const state: any = getState();
      const { lastFetchParams, loading } = state.AssetTrendDataSlice;

      // Prevent API call if already loading
      if (loading) {
        return false;
      }

      // If no previous params, allow the call
      if (!lastFetchParams) {
        return true;
      }

      // Compare parameters to check if anything has changed
      const hasChanged =
        lastFetchParams.assetId !== params.assetId ||
        lastFetchParams.startDate?.getTime() !== params.startDate?.getTime() ||
        lastFetchParams.endDate?.getTime() !== params.endDate?.getTime() ||
        lastFetchParams.aggregate !== params.aggregate ||
        lastFetchParams.aggregateMethod !== params.aggregateMethod ||
        lastFetchParams.retrieveMinMax !== params.retrieveMinMax ||
        lastFetchParams.fetchCov !== params.fetchCov ||
        lastFetchParams.Addresses !== params.Addresses ||
        params.isZoomedIn; // consider zoom state in change detection

      return hasChanged;
    },
  },
);

export const {
  setInitialState,
  resetAssetTrendDataState,
  setDateRangeAndAggregate,
  setChartViewOptions,
  setIsCustomRange,
  addLiveDataPoints,
  setLiveDataEnabled,
  setEnableLiveToggle,
  setLiveDataTimeRange,
  setChartsZoomState,
} = AssetTrendDataSlice.actions;

export default AssetTrendDataSlice.reducer;
